// Stripe devuelve el motivo del rechazo en inglés y sin acción: `code` es la categoría
// (`card_declined`, `expired_card`) y `decline_code` el motivo del emisor
// (`insufficient_funds`, `pickup_card`). Mostrar ese texto crudo deja al usuario reintentando
// a ciegas con más tarjetas — `insufficient_funds` es el 66% de nuestras fallas de cobro y su
// salida no es otra tarjeta, es transferencia. Esta es la única fuente de copy de rechazo del
// checkout: `decline_code` gana por ser más específico y nunca caemos al mensaje de Stripe.

const RETRY_OR_TRANSFER =
	'Prueba con otra tarjeta o paga por transferencia.';
const CONTACT_BANK = 'Comunícate con tu banco o prueba con otra tarjeta.';

const DECLINE_COPY: Record<string, string> = {
	insufficient_funds: `Tu tarjeta no tiene saldo suficiente. ${RETRY_OR_TRANSFER}`,
	card_velocity_exceeded: `Tu banco bloqueó la tarjeta por demasiados intentos seguidos. ${RETRY_OR_TRANSFER}`,
	withdrawal_count_limit_exceeded: `Tu tarjeta llegó a su límite de compras. ${RETRY_OR_TRANSFER}`,

	pickup_card: CONTACT_BANK,
	lost_card: CONTACT_BANK,
	stolen_card: CONTACT_BANK,
	restricted_card: CONTACT_BANK,
	do_not_honor: `Tu banco rechazó el pago. ${CONTACT_BANK}`,
	transaction_not_allowed: `Tu banco no permite este tipo de compra. ${CONTACT_BANK}`,
	not_permitted: `Tu banco no permite este tipo de compra. ${CONTACT_BANK}`,
	call_issuer: 'Tu banco necesita que lo contactes para autorizar el pago.',
	invalid_account: 'Los datos de la tarjeta no corresponden a una cuenta activa. Revísalos o usa otra tarjeta.',

	expired_card: 'Tu tarjeta está vencida. Usa otra tarjeta.',
	incorrect_number: 'El número de tarjeta no es correcto. Revísalo e intenta de nuevo.',
	incorrect_cvc: 'El código de seguridad no es correcto. Revísalo e intenta de nuevo.',
	invalid_cvc: 'El código de seguridad no es correcto. Revísalo e intenta de nuevo.',
	incorrect_zip: 'El código postal no coincide con el de tu tarjeta. Revísalo e intenta de nuevo.',
	invalid_expiry_month: 'La fecha de vencimiento no es correcta. Revísala e intenta de nuevo.',
	invalid_expiry_year: 'La fecha de vencimiento no es correcta. Revísala e intenta de nuevo.',
	invalid_amount: `No pudimos cobrar este monto con tu tarjeta. ${RETRY_OR_TRANSFER}`,

	payment_intent_authentication_failure:
		'Tu banco no confirmó el pago. Intenta de nuevo y completa la verificación que te pide tu banco.',
	authentication_required:
		'Tu banco pide que confirmes el pago. Intenta de nuevo y completa la verificación que te pide tu banco.',

	processing_error: 'Hubo un problema al procesar el pago. Intenta de nuevo en unos minutos.',
	try_again_later: 'Tu banco rechazó el pago de forma temporal. Intenta de nuevo en unos minutos.',
	issuer_not_available: 'No pudimos comunicarnos con tu banco. Intenta de nuevo en unos minutos.',

	generic_decline: `No pudimos procesar el pago con esta tarjeta. ${RETRY_OR_TRANSFER}`,
	card_declined: `No pudimos procesar el pago con esta tarjeta. ${RETRY_OR_TRANSFER}`,
};

const DEFAULT_DECLINE_MESSAGE = `No pudimos procesar tu pago. ${RETRY_OR_TRANSFER}`;

// A charge that only takes a card (the plan change's adjustment) cannot point to a transfer.
const RETRY_WITH_CARD = 'Prueba con otra tarjeta.';

export const resolveDeclineMessage = (
	code?: string | null,
	declineCode?: string | null,
	{ cardOnly = false }: { cardOnly?: boolean } = {},
): string => {
	const message =
		DECLINE_COPY[declineCode ?? ''] ??
		DECLINE_COPY[code ?? ''] ??
		DEFAULT_DECLINE_MESSAGE;
	return cardOnly ? message.replace(RETRY_OR_TRANSFER, RETRY_WITH_CARD) : message;
};

// Los 4xx del engine traen `error_code`/`decline_code` cuando Stripe rechazó la tarjeta
// (adelanto de pago, renew-attempt). Sin ellos: undefined y cada pantalla conserva su copy.
export const declineMessageFromError = (
	data?: { error_code?: string | null; decline_code?: string | null } | null,
): string | undefined =>
	data?.error_code
		? resolveDeclineMessage(data.error_code, data.decline_code)
		: undefined;
